class CustomAccordion extends HTMLElement {
  constructor() {
    super();
    this.options = {
      selector: '.accordion-item',
      headerSelector: '.accordion-header',
      contentSelector: '.accordion-content',
      disableOnDesktop: this.hasAttribute('disable-on-desktop'),
      closeOthers: this.hasAttribute('close-others'),
      expandAll: this.hasAttribute('expand-all'),
      firstOpen: this.hasAttribute('first-open'),
      desktopBreakpoint: parseInt(this.getAttribute('desktop-breakpoint')) || 1024
    };
    this.focusableElements = null;
    this.keyboardOpened = new Set(); // Track which items were opened via keyboard
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.items = this.querySelectorAll(this.options.selector);
    this.setupAriaAttributes();
    this.bindEvents();

    if (this.options.expandAll) {
      this.items.forEach(item => {
        item.classList.add('active');
        this.updateAriaAttributes(item, true);
        const content = item.querySelector(this.options.contentSelector);
        this.updateContentFocusability(content, true);
      });
    } else if (this.options.firstOpen && this.items.length > 0) {
      const firstItem = this.items[0];
      firstItem.classList.add('active');
      this.updateAriaAttributes(firstItem, true);
      const content = firstItem.querySelector(this.options.contentSelector);
      this.updateContentFocusability(content, true);
    }

    this.checkViewport();

    window.addEventListener('resize', () => {
      this.checkViewport();
    });
  }

  setupAriaAttributes() {
    // Set the accordion container role
    this.setAttribute('role', 'region');

    this.items.forEach((item, index) => {
      const header = item.querySelector(this.options.headerSelector);
      const content = item.querySelector(this.options.contentSelector);
      const id = `accordion-content-${index}`;

      // Set header attributes
      header.setAttribute('role', 'button');
      header.setAttribute('aria-expanded', 'false');
      header.setAttribute('aria-controls', id);
      header.setAttribute('tabindex', '0');

      // Set content attributes
      content.setAttribute('role', 'region');
      content.setAttribute('id', id);
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('tabindex', '-1');

      // Make all focusable elements non-focusable initially
      this.updateContentFocusability(content, false);
    });
  }

  updateContentFocusability(content, isFocusable) {
    const focusableElements = content.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    );

    focusableElements.forEach(element => {
      if (isFocusable) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  bindEvents() {
    this.items.forEach(item => {
      const header = item.querySelector(this.options.headerSelector);
      const content = item.querySelector(this.options.contentSelector);

      header.addEventListener('click', () => this.toggleAccordion(item, false));

      // Add keyboard support
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          this.toggleAccordion(item, true);
        }
      });

      // Handle focus trapping for both header and content
      header.addEventListener('keydown', (e) => {
        // Skip focus trapping if disabled on desktop, expandAll is enabled, or item wasn't opened via keyboard
        if ((this.options.disableOnDesktop && window.innerWidth >= this.options.desktopBreakpoint) || 
            this.options.expandAll || 
            !this.keyboardOpened.has(item)) {
          return;
        }

        if (e.key === 'Tab' && e.shiftKey && item.classList.contains('active')) {
          e.preventDefault();
          e.stopPropagation();
          const focusableElements = this.getFocusableElements(content);
          if (focusableElements.length) {
            focusableElements[focusableElements.length - 1].focus();
          }
        }
      });

      content.addEventListener('keydown', (e) => {
        // Skip focus trapping if disabled on desktop, expandAll is enabled, or item wasn't opened via keyboard
        if ((this.options.disableOnDesktop && window.innerWidth >= this.options.desktopBreakpoint) || 
            this.options.expandAll || 
            !this.keyboardOpened.has(item)) {
          return;
        }

        if (!item.classList.contains('active')) return;

        const focusableElements = this.getFocusableElements(content);
        if (!focusableElements.length) return;

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (e.key === 'Tab') {
          if (e.shiftKey) { // Shift + Tab
            if (document.activeElement === firstFocusable) {
              e.preventDefault();
              e.stopPropagation();
              header.focus();
            }
          } else { // Tab
            if (document.activeElement === lastFocusable) {
              e.preventDefault();
              e.stopPropagation();
              header.focus();
            }
          }
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation(); // Prevent slide-drawer from also handling Escape
          this.toggleAccordion(item, true);
          header.focus();
        }
      });
    });
  }

  getFocusableElements(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
  }

  checkViewport() {
    const isDesktop = window.innerWidth >= this.options.desktopBreakpoint;
    if (this.options.disableOnDesktop && isDesktop) {
      this.items.forEach(item => {
        item.classList.add('active');
        this.updateAriaAttributes(item, true);
        // Make all content focusable on desktop when disabled
        const content = item.querySelector(this.options.contentSelector);
        this.updateContentFocusability(content, true);
      });
    } else {
      this.items.forEach(item => {
        if (!item.classList.contains('active')) {
          item.classList.remove('active');
          this.updateAriaAttributes(item, false);
          const content = item.querySelector(this.options.contentSelector);
          this.updateContentFocusability(content, false);
        }
      });
    }
  }

  toggleAccordion(item, isKeyboardInteraction = false) {
    if (this.options.disableOnDesktop && window.innerWidth >= this.options.desktopBreakpoint) {
      return;
    }

    const isActive = item.classList.contains('active');
    const content = item.querySelector(this.options.contentSelector);

    if (this.options.closeOthers && !isActive) {
      this.items.forEach(otherItem => {
        if (otherItem !== item && otherItem.classList.contains('active')) {
          otherItem.classList.remove('active');
          this.updateAriaAttributes(otherItem, false);
          const otherContent = otherItem.querySelector(this.options.contentSelector);
          this.updateContentFocusability(otherContent, false);
          // Remove from keyboard opened set when closing
          this.keyboardOpened.delete(otherItem);
        }
      });
    }

    item.classList.toggle('active');
    const newIsActive = item.classList.contains('active');
    this.updateAriaAttributes(item, newIsActive);
    this.updateContentFocusability(content, newIsActive);

    // Track keyboard interactions
    if (newIsActive && isKeyboardInteraction) {
      this.keyboardOpened.add(item);
    } else if (!newIsActive) {
      this.keyboardOpened.delete(item);
    }

    // Only auto-focus first element if opened via keyboard
    if (newIsActive && isKeyboardInteraction) {
      const focusableElements = this.getFocusableElements(content);
      if (focusableElements.length) {
        focusableElements[0].focus();
      }
    }
  }

  updateAriaAttributes(item, isExpanded) {
    const header = item.querySelector(this.options.headerSelector);
    const content = item.querySelector(this.options.contentSelector);
    header.setAttribute('aria-expanded', isExpanded.toString());
    content.setAttribute('aria-hidden', (!isExpanded).toString());
    content.setAttribute('tabindex', isExpanded ? '0' : '-1');
  }
}

customElements.define('custom-accordion', CustomAccordion);
